import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DialogPresets,
  InputSearchFilter,
  ProfileImageService,
  toSearchStream,
  TrackByFunctions,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { Brand, Staffer, User } from '@principle-theorem/principle-core';
import {
  IBrand,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  all$,
  asyncForEach,
  type CollectionReference,
  DATE_TIME_WITH_YEAR_FORMAT,
  DocumentReference,
  Firestore,
  getError,
  multiFilter,
  snapshot,
  toNamedDocument,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sortBy } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  type Observable,
  ReplaySubject,
} from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  type IInviteUserDialogData,
  type InviteFormData,
  InviteUserDialogComponent,
} from '../invite-user-dialog/invite-user-dialog.component';

@Component({
  selector: 'pr-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UsersComponent {
  profileImage = inject(ProfileImageService);
  trackByUser = TrackByFunctions.ref<WithRef<IUser>>();
  usersFilter: InputSearchFilter<WithRef<IUser>>;
  activeUsers$: Observable<WithRef<IUser>[]>;
  disabledUsers$: Observable<WithRef<IUser>[]>;
  searchCtrl: TypedFormControl<string> = new TypedFormControl<string>();
  userCollection$ = new ReplaySubject<CollectionReference<IUser>>(1);
  emptyState$: Observable<boolean>;
  emptySearch$: Observable<boolean>;
  readonly dateFormat = DATE_TIME_WITH_YEAR_FORMAT;
  routePrefix$ = new BehaviorSubject<string>('');

  @Input()
  set routePrefix(routePrefix: string) {
    if (routePrefix) {
      this.routePrefix$.next(routePrefix);
    }
  }

  constructor(
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar
  ) {
    const users$ = this.userCollection$.pipe(
      switchMap((collection) => all$(collection)),
      map((users) => sortBy(users, 'name'))
    );
    this.usersFilter = new InputSearchFilter<WithRef<IUser>>(
      users$,
      toSearchStream(this.searchCtrl),
      ['name', 'email']
    );
    this.activeUsers$ = this.usersFilter.results$.pipe(
      multiFilter((user) => user.isEnabled)
    );
    this.disabledUsers$ = this.usersFilter.results$.pipe(
      multiFilter((user) => !user.isEnabled)
    );
    this.emptyState$ = users$.pipe(map((users) => !users.length));
    this.emptySearch$ = combineLatest([
      this.usersFilter.results$,
      this.emptyState$,
    ]).pipe(map(([results, emptyState]) => !results.length && !emptyState));
  }

  @Input()
  set userCollection(userCollection: CollectionReference<IUser>) {
    if (userCollection) {
      this.userCollection$.next(userCollection);
    }
  }

  async invite(): Promise<void> {
    const usersCollection = await snapshot(this.userCollection$);
    const data: IInviteUserDialogData = { usersCollection };
    const users = await this._dialog
      .open<InviteUserDialogComponent, IInviteUserDialogData, InviteFormData[]>(
        InviteUserDialogComponent,
        DialogPresets.large({ data })
      )
      .afterClosed()
      .toPromise();

    if (users?.length) {
      await this._handleInvite(users);
    }
  }

  getRoute$(userId: string): Observable<string[]> {
    return this.routePrefix$.pipe(
      map((routePrefix) => compact(['./', routePrefix, userId]))
    );
  }

  private async _handleInvite(users: InviteFormData[]): Promise<void> {
    const collection = await snapshot(this.userCollection$);
    await asyncForEach(users, async (user) => {
      const existingUser = await Firestore.firstResult(
        collection,
        where('email', '==', user.email)
      );
      if (existingUser) {
        this._snackBar.open(
          `A user already exists for the email address ${user.email}`
        );
        return;
      }

      const userRef = await Firestore.addDoc(collection, User.init(user));
      const userWithRef = await Firestore.getDoc(userRef);
      await this._createStaffer(userWithRef, user.brands);
    });
    this._snackBar.open(users.length > 1 ? `Invites Sent` : `Invite Sent`);
  }

  private async _createStaffer(
    user: WithRef<IUser>,
    brandRefs: DocumentReference<IBrand>[]
  ): Promise<void> {
    try {
      await asyncForEach(brandRefs, async (brandRef) => {
        const brand = await Firestore.getDoc(brandRef);
        const existingStaffer = await snapshot(
          Brand.userStaffer$(brand, user, false)
        );

        if (!existingStaffer) {
          await Firestore.addDoc(
            Brand.stafferCol(brand),
            Staffer.init({ user: toNamedDocument(user) })
          );
          return;
        }

        if (existingStaffer.deleted) {
          await Firestore.patchDoc(existingStaffer.ref, {
            deleted: false,
          });
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to create staffer document for user',
        getError(error)
      );
    }
  }
}
